import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { ContractAnnotationsSidebar } from "./ContractAnnotationsSidebar";
import { anchorFromSelection, findNearestContractAnchorKey } from "../utils/contractAnnotations";

const QUICK_EMOJIS = ["👍", "👎", "✅", "❓", "💬"];

function cx(base, extra) {
  return extra ? `${base} ${extra}`.trim() : base;
}

function IconCommentPlus() {
  return /* @__PURE__ */ jsx("svg", {
    className: "w-5 h-5",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    children: [
      /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" }),
      /* @__PURE__ */ jsx("path", { strokeLinecap: "round", d: "M12 8v8M8 12h8" }),
    ],
  });
}

function IconSmile() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-5 h-5", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
    /* @__PURE__ */ jsx("path", { strokeLinecap: "round", d: "M8 14s1.5 2 4 2 4-2 4-2" }),
    /* @__PURE__ */ jsx("path", { strokeLinecap: "round", d: "M9 9h.01M15 9h.01" }),
  ] });
}

function IconSuggest() {
  return /* @__PURE__ */ jsxs("svg", { className: "w-5 h-5", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [
    /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" }),
    /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" }),
  ] });
}

/**
 * Gray “desk” + white page, optional Docs-style floating pill on text selection, and comments sidebar.
 */
export function ContractGoogleDocsShell({
  children,
  enableSelectionToolbar = false,
  annotations = [],
  selectedAnchorKey = "",
  onSelectAnchor,
  onPostAnnotation,
  showSidebar = true,
  canReplySidebar = false,
  canResolveSidebar = false,
  onReply,
  onResolve,
  userDisplayName = "You",
  userInitial = "U",
  emptySidebarHint = "No comments yet.",
  shellOuterClassName = "",
  deskClassName = "",
  paperClassName = "",
}) {
  const paperRef = useRef(null);
  const selectionRef = useRef(null);
  const [toolbar, setToolbar] = useState(null);
  const [card, setCard] = useState(null);
  const [draft, setDraft] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);

  const clearFloating = useCallback(() => {
    setToolbar(null);
    setCard(null);
    setDraft("");
    setEmojiOpen(false);
    selectionRef.current = null;
  }, []);

  const handleMouseUp = useCallback(
    (e) => {
      if (!enableSelectionToolbar) return;
      if (e?.target?.closest?.("[data-contract-float]")) return;

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const inPaper = paperRef.current?.contains(range.commonAncestorContainer);

      if (sel.isCollapsed) {
        if (!paperRef.current?.contains(e.target) && !e.target.closest?.("[data-contract-float]")) clearFloating();
        return;
      }

      if (!inPaper) return;

      const text = sel.toString().trim();
      if (text.length < 1) {
        clearFloating();
        return;
      }
      const common = range.commonAncestorContainer;
      const el = common.nodeType === 1 ? common : common.parentElement;
      const baseKey = findNearestContractAnchorKey(el);
      const rect = range.getBoundingClientRect();
      if (rect.width < 2 && rect.height < 2) {
        clearFloating();
        return;
      }
      selectionRef.current = { baseKey, text };
      setToolbar({
        top: rect.top + rect.height / 2,
        left: Math.min(window.innerWidth - 64, rect.right + 8),
      });
      setCard(null);
      setDraft("");
      setEmojiOpen(false);
    },
    [enableSelectionToolbar, clearFloating]
  );

  useEffect(() => {
    if (!enableSelectionToolbar) return;
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [enableSelectionToolbar, handleMouseUp]);

  useEffect(() => {
    const onScroll = () => {
      if (toolbar) clearFloating();
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [toolbar, clearFloating]);

  useEffect(() => {
    if (!enableSelectionToolbar) return;
    const onKey = (e) => {
      if (e.key === "Escape") clearFloating();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enableSelectionToolbar, clearFloating]);

  const openCard = (mode) => {
    if (!selectionRef.current) return;
    setCard({ mode });
    setDraft("");
    setEmojiOpen(false);
  };

  const submitCard = () => {
    const snap = selectionRef.current;
    if (!snap || !card) return;
    const t = draft.trim();
    if (!t) return;
    const anchor = anchorFromSelection(snap.baseKey, snap.text);
    const kind = card.mode === "suggest" ? "suggestion" : "comment";
    onPostAnnotation({ anchor, body: t, kind });
    clearFloating();
    window.getSelection()?.removeAllRanges();
  };

  const pickEmoji = (emoji) => {
    const snap = selectionRef.current;
    if (!snap) return;
    const anchor = anchorFromSelection(snap.baseKey, snap.text);
    onPostAnnotation({ anchor, body: emoji, kind: "reaction" });
    clearFloating();
    setEmojiOpen(false);
    window.getSelection()?.removeAllRanges();
  };

  const initial = (userInitial || "U").slice(0, 1).toUpperCase();
  const name = userDisplayName || "You";

  return /* @__PURE__ */ jsxs("div", {
    className: cx(
      "flex min-h-0 w-full min-w-0 flex-1 flex-col gap-6 overflow-hidden lg:flex-row lg:items-stretch lg:justify-center",
      shellOuterClassName
    ),
    children: [
      /* @__PURE__ */ jsx("div", {
        className: cx(
          "flex min-h-0 min-w-0 flex-1 justify-center bg-[color:var(--color-surface-sunken)] p-6 sm:p-8 lg:max-h-full lg:overflow-y-auto rounded-2xl",
          deskClassName
        ),
        children: /* @__PURE__ */ jsxs("div", {
          ref: paperRef,
          className: cx(
            "relative bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12),0_4px_24px_rgba(0,0,0,0.06)] w-full max-w-[816px] min-h-[480px] px-10 sm:px-14 py-12 sm:py-14 text-slate-900 rounded-sm border border-slate-200/80",
            paperClassName
          ),
          children: [
            children,
            enableSelectionToolbar &&
              toolbar &&
              !card &&
              /* @__PURE__ */ jsxs("div", {
                "data-contract-float": "1",
                className:
                  "fixed z-[60] flex flex-col gap-0.5 bg-[color:var(--color-bg-white)] rounded-full shadow-lg border border-[color:var(--color-border)] py-2 px-1.5",
                style: { top: Math.max(8, toolbar.top - 52), left: toolbar.left },
                children: [
                  /* @__PURE__ */ jsx("button", {
                    type: "button",
                    title: "Add comment",
                    onClick: () => openCard("comment"),
                    className: "p-2 rounded-full hover:bg-[color:var(--color-bg-alt)] text-[color:var(--color-accent)] transition-colors",
                    children: /* @__PURE__ */ jsx(IconCommentPlus, {}),
                  }),
                  /* @__PURE__ */ jsx("button", {
                    type: "button",
                    title: "Add emoji reaction",
                    onClick: () => setEmojiOpen((o) => !o),
                    className: "p-2 rounded-full hover:bg-[color:var(--color-bg-alt)] text-[color:var(--color-accent)] transition-colors",
                    children: /* @__PURE__ */ jsx(IconSmile, {}),
                  }),
                  /* @__PURE__ */ jsx("button", {
                    type: "button",
                    title: "Suggest edits",
                    onClick: () => openCard("suggest"),
                    className: "p-2 rounded-full hover:bg-[color:var(--color-bg-alt)] text-[color:var(--color-accent)] transition-colors",
                    children: /* @__PURE__ */ jsx(IconSuggest, {}),
                  }),
                ],
              }),
            enableSelectionToolbar &&
              emojiOpen &&
              toolbar &&
              /* @__PURE__ */ jsx("div", {
                "data-contract-float": "1",
                className: "fixed z-[61] flex gap-1 bg-[color:var(--color-bg-white)] rounded-xl shadow-xl border border-[color:var(--color-border)] p-2",
                style: { top: Math.max(8, toolbar.top + 8), left: Math.max(8, toolbar.left - 24) },
                children: QUICK_EMOJIS.map((em) =>
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      onClick: () => pickEmoji(em),
                      className: "text-xl w-10 h-10 rounded-lg hover:bg-[color:var(--color-bg-alt)]",
                      children: em,
                    },
                    em
                  )
                ),
              }),
            enableSelectionToolbar &&
              card &&
              toolbar &&
              /* @__PURE__ */ jsxs("div", {
                "data-contract-float": "1",
                className:
                  "fixed z-[62] w-[min(360px,calc(100vw-24px))] bg-[color:var(--color-bg-white)] rounded-xl shadow-2xl border border-[color:var(--color-border)] p-4",
                style: { top: Math.max(8, toolbar.top - 24), left: Math.min(window.innerWidth - 372, toolbar.left + 52) },
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "flex items-center gap-2 mb-3",
                    children: [
                      /* @__PURE__ */ jsx("div", {
                        className: "w-8 h-8 rounded-full bg-[color:var(--color-accent-fill)] text-white text-sm font-bold flex items-center justify-center shrink-0",
                        children: initial,
                      }),
                      /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold text-[color:var(--color-text-hero)] truncate", children: name }),
                    ],
                  }),
                  selectionRef.current &&
                    /* @__PURE__ */ jsx("p", {
                      className:
                        "text-[11px] text-[color:var(--color-text-muted)] bg-[color:var(--color-warn-bg)] border border-[color:var(--color-warn-line)] rounded-lg px-2 py-1.5 mb-3 max-h-20 overflow-y-auto whitespace-pre-wrap",
                      children: selectionRef.current.text,
                    }),
                  /* @__PURE__ */ jsx("textarea", {
                    value: draft,
                    onChange: (e) => setDraft(e.target.value),
                    placeholder: card.mode === "suggest" ? "Suggest a change\u2026" : "Comment or add others with @",
                    rows: 3,
                    className: "w-full text-sm border border-[color:var(--color-border)] rounded-lg px-3 py-2 outline-none bg-transparent text-[color:var(--color-text-hero)] focus:ring-2 focus:ring-[color:var(--color-info-line)] resize-y min-h-[72px]",
                  }),
                  /* @__PURE__ */ jsxs("div", {
                    className: "flex justify-end gap-2 mt-3",
                    children: [
                      /* @__PURE__ */ jsx("button", {
                        type: "button",
                        onClick: clearFloating,
                        className: "px-3 py-1.5 text-sm font-medium text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-bg-alt)] rounded-lg",
                        children: "Cancel",
                      }),
                      /* @__PURE__ */ jsx("button", {
                        type: "button",
                        disabled: !draft.trim(),
                        onClick: submitCard,
                        className: `px-4 py-1.5 text-sm font-semibold rounded-lg ${draft.trim() ? "bg-[color:var(--color-accent-fill)] text-white hover:opacity-90" : "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] cursor-not-allowed"}`,
                        children: "Comment",
                      }),
                    ],
                  }),
                ],
              }),
          ],
        }),
      }),
      showSidebar &&
        /* @__PURE__ */ jsx("div", {
          className:
            "flex min-h-0 w-full min-w-0 shrink-0 flex-col self-stretch overflow-hidden lg:max-h-full lg:w-[300px] xl:w-[320px] lg:max-w-[min(340px,100%)]",
          children: /* @__PURE__ */ jsx(ContractAnnotationsSidebar, {
            fillAvailableHeight: true,
            annotations,
            selectedAnchorKey,
            onSelectAnchor,
            onReply,
            onResolve,
            canReply: canReplySidebar,
            canResolve: canResolveSidebar,
            emptyHint: emptySidebarHint,
          }),
        }),
    ],
  });
}
